import { CSSProperties, FunctionComponent, HTMLAttributes, ReactNode } from 'react';
import { cssVars as palette } from '../../theme/cssVars';

type BadgeColor = 'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning';
type Anchor = { vertical: 'top' | 'bottom'; horizontal: 'left' | 'right' };

type BadgeUIProps = {
  badgeContent?: ReactNode,
  color?: BadgeColor,
  overlap?: 'rectangular' | 'circular',
  anchorOrigin?: Anchor,
  showZero?: boolean,
  children?: ReactNode,
  // Replaces MUI's `sx={{ '& .MuiBadge-badge': {...} }}` — styles the badge element.
  badgeStyle?: CSSProperties,
  badgeClassName?: string,
} & HTMLAttributes<HTMLSpanElement>;

const bgColor: Record<BadgeColor, string> = {
  default: 'var(--ds-border-strong)',
  primary: palette.primary,
  secondary: palette.secondary,
  error: palette.error,
  info: palette.info,
  success: palette.success,
  warning: palette.warning,
};

// Corner placement — mirrors MUI's anchorOrigin + overlap transforms. Rectangular
// anchors at 0; circular at 14% so the badge rides the circle's edge.
const anchorStyle = (a: Anchor, overlap: 'rectangular' | 'circular', invisible: boolean): CSSProperties => {
  const edge = overlap === 'circular' ? '14%' : '0';
  const tx = a.horizontal === 'right' ? '50%' : '-50%';
  const ty = a.vertical === 'top' ? '-50%' : '50%';
  const scale = invisible ? 0 : 1;
  return {
    ...(a.vertical === 'top' ? { top: edge } : { bottom: edge }),
    ...(a.horizontal === 'right' ? { right: edge } : { left: edge }),
    transform: `scale(${scale}) translate(${tx}, ${ty})`,
    transformOrigin: `${a.horizontal === 'right' ? '100%' : '0%'} ${a.vertical === 'top' ? '0%' : '100%'}`,
  };
};

const BadgeUI: FunctionComponent<BadgeUIProps> = ({
  badgeContent,
  color = 'default',
  overlap = 'rectangular',
  anchorOrigin = { vertical: 'top', horizontal: 'right' },
  showZero = false,
  className = '',
  style,
  badgeStyle,
  badgeClassName = '',
  children,
  ...rest
}) => {
  const invisible = badgeContent === undefined || badgeContent === null || (badgeContent === 0 && !showZero);

  const rootStyle: CSSProperties = {
    position: 'relative',
    display: 'inline-flex',
    verticalAlign: 'middle',
    flexShrink: 0,
    ...style,
  };

  const badge: CSSProperties = {
    position: 'absolute',
    display: 'flex',
    flexWrap: 'wrap',
    justifyContent: 'center',
    alignItems: 'center',
    boxSizing: 'border-box',
    height: 20,
    minWidth: 20,
    lineHeight: 1,
    padding: '0 6px',
    fontSize: '0.75rem',
    fontWeight: 500,
    borderRadius: 10,
    zIndex: 1,
    backgroundColor: bgColor[color],
    color: palette.backgroundPaper,
    transition: 'transform 225ms cubic-bezier(0.4, 0, 0.2, 1)',
    ...anchorStyle(anchorOrigin, overlap, invisible),
    // call-site badgeStyle wins (replaces MUI's `& .MuiBadge-badge` sx)
    ...badgeStyle,
  };

  return (
    <span className={className} style={rootStyle} {...rest}>
      {children}
      <span className={badgeClassName} style={badge}>{badgeContent}</span>
    </span>
  );
};

export default BadgeUI;
